import React, { useEffect } from 'react'
import {
  Grid,
  Heading,
  Form,
  ExpandableSection,
  Input,
} from '@enterprise-ui/canvas-ui-react'
import '../../Cases/Custom.css'
import { Autocomplete } from '@enterprise-ui/canvas-ui-react-autocomplete'
import { FormikValues } from 'formik'
import { CommonSearch } from './CommonSearch'
import { useGlobalForm } from '../Context/GlobalFormStateContext'
import { SET_FIELD } from './Constants'
import { findRequired } from '../Services/CheckRequired'
import { CaseType, CaseSubType } from '../Services/ConstantData'
import { useProfile } from '../../Layout/UserProfile/UserProfileProvider'
import _ from 'lodash'
import { CheckCircleIcon, EnterpriseIcon } from '@enterprise-ui/icons'
import { useEnv } from '@praxis/component-runtime-env'
import { EnvConfig } from '../../../config/appConfig'
import {
  VI19_SUB_REASON_CODES,
  VI18_REASON_CODE,
  VI19_REASON_CODE,
  VI19_SUB_REASON_CATEGORIES,
  REASON_CODE_CONFIG,
  RTVS_MULTIPLE_REASONS_CODE_TEXT,
  RTVS_DOC_PREFIX,
} from '../Services/ConstantData'
import CreateCurrencyInput from './CreateCurrencyInput'
import CreateSubReasonCodeText2Input from './CreateSubReasonCodeText2Input'
import CreatePaymentMethodSelect from './CreatePaymentMethodSelect'
interface MainProp {
  heading: String
  caseSubTypeValues: any
  docTypeValues?: any
  docSubTypeValues?: any
  reasonCodeValues: any
  formik: FormikValues
  handleSearch: () => void
  importVendor?: boolean
}

const DisputeDetails: React.FC<MainProp> = ({
  heading,
  caseSubTypeValues,
  docTypeValues,
  docSubTypeValues,
  reasonCodeValues,
  formik,
  handleSearch,
  importVendor,
}) => {
  const [state, dispatch] = useGlobalForm()
  const { userProfile } = useProfile()
  const env = useEnv() as EnvConfig
  const isVendor = userProfile?.isVendor
  const formData = formik.values
  const gridSize = importVendor ? 10 : 5
  const documentLabel =
    formData?.caseType === CaseType.INTERNALPROCESSING && importVendor
      ? 'Reference #'
      : importVendor
        ? 'Claim #'
        : 'Document'
  const caseTypeLabel =
    formData?.caseType !== CaseType.REQUESTFORINFO && importVendor
      ? 'Claim Type'
      : formData?.caseType === CaseType.REQUESTFORINFO
        ? 'Request Type'
        : formData?.caseType === CaseType.DISPUTE
          ? 'Deduction/Invoice Type'
          : 'Case Sub Type'
  const documentTypeLabel =
    formData?.caseType === CaseType.DISPUTE
      ? 'Claim / Doc Type'
      : 'Choose Document Type'
  const documentSubTypeLabel =
    formData?.caseType === CaseType.DISPUTE
      ? 'Claim / Doc Sub Type'
      : 'Choose Document Sub Type'
  const reasonLabel =
    formData?.caseType === CaseType.REQUESTFORINFO
      ? 'Reason for Request?'
      : formData?.caseType === CaseType.DISPUTE
        ? 'Reason for Disputing?'
        : 'Reason for Internal Processing'
  const searchType =
    importVendor && formData?.caseType !== CaseType.INTERNALPROCESSING
      ? 'number'
      : 'text'

  const getReasonConfig = (reasonCode: string, subReasonCode?: string) => {
    if (reasonCode === VI18_REASON_CODE) {
      return REASON_CODE_CONFIG[VI18_REASON_CODE]
    }

    if (reasonCode === VI19_REASON_CODE && subReasonCode) {
      return REASON_CODE_CONFIG[VI19_REASON_CODE]?.[subReasonCode]
    }

    return { editLabel: '', helperText: '', viewLabel: '' }
  }

  const reasonConfig = getReasonConfig(
    formData?.reasonCode,
    formData?.subReasonCode,
  )

  const numericSubReasons = VI19_SUB_REASON_CATEGORIES.NUMERIC
  const textSubReasons = VI19_SUB_REASON_CATEGORIES.TEXT

  const subReasonCodeText2Type =
    formData?.reasonCode === VI19_REASON_CODE &&
    numericSubReasons.includes(formData?.subReasonCode)
      ? 'number'
      : formData?.reasonCode === VI18_REASON_CODE ||
          (formData?.reasonCode === VI19_REASON_CODE &&
            textSubReasons.includes(formData?.subReasonCode))
        ? 'text'
        : 'text'
  const subReasonCodeText2Label = reasonConfig.editLabel
  const subReasonCodeText2HelperText = reasonConfig.helperText

  const currentDocType = docTypeValues?.find(
    (docTypeValues: any) =>
      Number(docTypeValues?.id) === Number(formik.values.documentTypeId),
  )
  const currentDocSubType = docSubTypeValues?.find(
    (docSubTypeValues: any) =>
      Number(docSubTypeValues?.id) === Number(formik.values?.documentSubTypeId),
  )
  const isRTVSDoc = formData?.originalDocumentNumber
    ?.toUpperCase()
    .startsWith(RTVS_DOC_PREFIX)
  const currentReasonCode =
    isRTVSDoc && formik?.values?.reasonCode === RTVS_MULTIPLE_REASONS_CODE_TEXT
      ? {
          id: RTVS_MULTIPLE_REASONS_CODE_TEXT,
          value: RTVS_MULTIPLE_REASONS_CODE_TEXT,
          label: RTVS_MULTIPLE_REASONS_CODE_TEXT,
        }
      : reasonCodeValues?.find(
          (reasonCodeList: any) =>
            reasonCodeList?.id === formik?.values?.reasonCode,
        )

  const docTypeRequired = findRequired(formik.values)?.DocTypeReqFlag
  const reasonCodeRequired = findRequired(formik.values)?.reasonCode
  function clearValues(event: any) {
    formik.setFieldValue('originalDocumentNumber', event.target.value)
    if (formData?.caseType !== CaseType.REQUESTFORINFO) {
      dispatch({
        type: SET_FIELD,
        payload: [
          { id: 'documentHintText', value: '' },
          { id: 'documentFound', value: false },
          { id: 'isDocNextFlag', value: false },
        ],
      })
    } else {
      dispatch({
        type: SET_FIELD,
        payload: [{ id: 'documentHintText', value: '' }],
      })
    }
    formik.setErrors({})
  }

  const contractErrors = env.vi.contractErrors.map((contractError) => ({
    id: contractError.value,
    value: contractError.value,
    label: contractError.label,
  }))

  useEffect(() => {
    formik.setFieldValue('subReasonCodeText2', undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.subReasonCode, formik.values.reasonCode])

  useEffect(() => {
    if (
      formData?.originalDocumentNumber
        ?.toUpperCase()
        .startsWith(RTVS_DOC_PREFIX)
    ) {
      formik.setFieldValue('reasonCode', RTVS_MULTIPLE_REASONS_CODE_TEXT)
      formik.setFieldValue('reasonCodeText', RTVS_MULTIPLE_REASONS_CODE_TEXT)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.originalDocumentNumber])

  useEffect(() => {
    dispatch({
      type: SET_FIELD,
      payload: [
        { id: 'caseType', value: formData?.caseType },
        { id: 'caseSubType', value: formData?.caseSubType },
      ],
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.caseSubType])

  useEffect(() => {
    if (reasonCodeValues?.length === 1 && !isRTVSDoc) {
      formik.setFieldValue('reasonCode', reasonCodeValues[0].value)
      formik.setFieldValue('reasonCodeText', reasonCodeValues[0].label)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reasonCodeValues])

  // Enable Next button when document is found and "Discount" reason code is selected
  useEffect(() => {
    // Check if the selected reason code is "Discount" or has "Discount" in the text
    const isDiscountReasonCode =
      formik.values.reasonCode !== undefined &&
      formik.values.reasonCode !== '' &&
      (formik.values.reasonCode === 'DISC' || // Check for DISC code
        formik.values.reasonCode === 'Discount' || // Check for exact match
        formik.values.reasonCodeText?.toLowerCase().includes('discount')) // Check for text containing discount

    if (state.documentFound && isDiscountReasonCode && !state.showDocuments) {
      dispatch({
        type: SET_FIELD,
        payload: [{ id: 'isDocNextFlag', value: true }],
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.reasonCode,
    formik.values.reasonCodeText,
    state.documentFound,
  ])

  // Helper function to determine if we should show the sub reason code field
  const shouldShowSubReasonCode = () => {
    return (
      formData?.caseType === CaseType.DISPUTE &&
      formik.values.caseSubType === CaseSubType.VENDOR_INCOME &&
      formik.values.reasonCode === VI19_REASON_CODE
    )
  }

  // Helper function to determine if we should show the second text field
  const shouldShowSubReasonCodeText2 = () => {
    if (
      formData?.caseType !== CaseType.DISPUTE ||
      formik.values.caseSubType !== CaseSubType.VENDOR_INCOME
    ) {
      return false
    }

    const { reasonCode, subReasonCode } = formik.values

    if (reasonCode === VI18_REASON_CODE) {
      return true
    }

    if (reasonCode === VI19_REASON_CODE) {
      return (
        subReasonCode !== undefined &&
        (numericSubReasons.includes(subReasonCode) ||
          textSubReasons.includes(subReasonCode))
      )
    }

    return false
  }

  // Helper to determine which input component to render
  const getSubReasonCodeText2Component = () => {
    const { reasonCode, subReasonCode } = formik.values

    // Currency input for AMOUNT_PER_UNIT
    if (
      reasonCode === VI19_REASON_CODE &&
      subReasonCode === VI19_SUB_REASON_CODES.AMOUNT_PER_UNIT
    ) {
      return (
        <CreateCurrencyInput
          formik={formik}
          state={state}
          subReasonCodeText2Label={subReasonCodeText2Label}
          subReasonCodeText2HelperText={subReasonCodeText2HelperText}
        />
      )
    }

    // Select input for PAYMENT_METHOD
    if (
      reasonCode === VI19_REASON_CODE &&
      subReasonCode === VI19_SUB_REASON_CODES.PAYMENT_METHOD
    ) {
      return (
        <CreatePaymentMethodSelect
          subReasonCodeText2Label={subReasonCodeText2Label}
          formik={formik}
          state={state}
          env={env}
        />
      )
    }

    // Standard text/number input for other cases
    if (shouldShowSubReasonCodeText2()) {
      return (
        <CreateSubReasonCodeText2Input
          subReasonCodeText2Label={subReasonCodeText2Label}
          subReasonCodeText2Type={subReasonCodeText2Type}
          formik={formik}
          state={state}
          numericSubReasons={numericSubReasons}
          textSubReasons={textSubReasons}
          subReasonCodeText2HelperText={subReasonCodeText2HelperText}
        />
      )
    }

    return null
  }

  return (
    <div className="hc-pa-normal hc-pa-none hc-ma-none">
      <ExpandableSection startExpanded toggleLocation="left" padding="dense">
        <Heading size={6}>
          {heading}{' '}
          {state.showDocuments && (
            <EnterpriseIcon
              icon={CheckCircleIcon}
              color="green"
              className="hc-va-sub"
            />
          )}
        </Heading>
        <ExpandableSection.Content className="hc-pt-none hc-mt-none">
          <Grid.Container className="hc-pt-none hc-ml-expanded">
            {formData?.caseType !== CaseType.REQUESTFORINFO ? (
              <CommonSearch
                buttonId="documentIdSearch"
                inputId="originalDocumentNumber"
                label={documentLabel}
                onChange={(event: any) => {
                  clearValues(event)
                }}
                onBlur={(e: any) => {
                  e.preventDefault()
                  handleSearch()
                }}
                value={_.toUpper(formik.values.originalDocumentNumber)}
                size={5}
                hintText={state.documentHintText}
                type={searchType}
                maxlength="22"
                validSearchIconCheck={state.showDocuments}
                inValidSearchIconCheck={state.isDocNextFlag}
                checkIconCheck={state.disputeSectionFlag}
                infoIconCheck={!state.disputeSectionFlag}
                errorFlag={formik.errors.originalDocumentNumber !== undefined}
                fieldErrorText={formik.errors.originalDocumentNumber}
                docDisable={state.documentFound}
                isRequired={!state.isCarrierVendor}
                isVendorSearch={false}
                focus={!state.isCarrierVendor}
              />
            ) : (
              <Grid.Item xs={10} md={5}>
                <Form.Field
                  id="originalDocumentNumber"
                  label={documentLabel}
                  className="inputtype hc-ta-left"
                  hintText={state.documentHintText}
                  type={searchType}
                  maxLength="22"
                  onChange={(event: any) => {
                    clearValues(event)
                  }}
                  onBlur={(e: any) => {
                    e.preventDefault()
                    handleSearch()
                  }}
                  value={_.toUpper(formData.originalDocumentNumber)}
                  error={formik.errors.originalDocumentNumber !== undefined}
                  errorText={formik.errors.originalDocumentNumber}
                  disabled={state.showDocuments}
                  autoFocus
                  required
                />
              </Grid.Item>
            )}
            <Grid.Item xs={10} md={5}>
              <Form.Field
                id="caseSubType"
                label={caseTypeLabel}
                type="select"
                className="hc-ta-left"
                value={formik.values.caseSubType}
                onUpdate={formik.setFieldValue}
                options={caseSubTypeValues}
                error={formik.errors.caseSubType !== undefined}
                errorText={formik.errors.caseSubType}
                disabled={state.showDocuments}
                required
              />
            </Grid.Item>
          </Grid.Container>
          <Grid.Container className="hc-ml-expanded">
            {(docTypeRequired === 'Y' ||
              (!isVendor &&
                !(
                  formData?.caseType === CaseType.DISPUTE &&
                  formik.values.caseSubType === CaseSubType.VENDOR_INCOME
                ))) && (
              <Grid.Item xs={10} md={gridSize} className="hc-pt-md hc-pb-none">
                <Autocomplete
                  id="documentTypeId"
                  label={documentTypeLabel}
                  onUpdate={(id, value) => {
                    formik.setFieldValue('documentTypeId', value?.value)
                    formik.setFieldValue('documentTypeText', value?.label)
                  }}
                  value={currentDocType}
                  options={docTypeValues}
                  error={formik.errors.documentTypeId !== undefined}
                  errorText={formik.errors.documentTypeId}
                  disabled={state.showDocuments}
                  required={importVendor}
                />
              </Grid.Item>
            )}
            {!isVendor &&
              !(
                formData?.caseType === CaseType.DISPUTE &&
                formik.values.caseSubType === CaseSubType.VENDOR_INCOME
              ) &&
              !importVendor && (
                <Grid.Item xs={10} md={5} className="hc-pt-md">
                  <Autocomplete
                    id="documentSubTypeId"
                    label={documentSubTypeLabel}
                    onUpdate={(id, value) => {
                      formik.setFieldValue('documentSubTypeId', value?.value)
                      formik.setFieldValue('documentSubTypeText', value?.label)
                    }}
                    value={currentDocSubType}
                    options={docSubTypeValues}
                    error={formik.errors.documentSubTypeId !== undefined}
                    errorText={formik.errors.documentSubTypeId}
                    disabled={state.showDocuments}
                    required={importVendor}
                  />
                </Grid.Item>
              )}
          </Grid.Container>
          {!isRTVSDoc && (
            <Grid.Container className="hc-ml-expanded">
              <Grid.Item xs={10} md={10} className="hc-pt-md">
                <Autocomplete
                  id="reasonCode"
                  options={reasonCodeValues}
                  label={reasonLabel}
                  onUpdate={(id, value) => {
                    formik.setFieldValue(id, value?.value)
                    formik.setFieldValue('reasonCodeText', value?.label)
                  }}
                  value={currentReasonCode}
                  disabled={state.showDocuments}
                  required={reasonCodeRequired === 'Y'}
                  error={formik.errors.reasonCode !== undefined}
                  errorText={formik.errors.reasonCode}
                />
              </Grid.Item>
            </Grid.Container>
          )}
          {/* New Code here */}
          {shouldShowSubReasonCode() && (
            <Grid.Container className="hc-pt-none hc-ml-expanded">
              <Grid.Item xs={10} md={10} className="hc-pt-md">
                <Autocomplete
                  id="subReasonCode"
                  options={contractErrors}
                  label={'What is the contract error?'}
                  onUpdate={(id, value) => {
                    formik.setFieldValue(id, value?.value)
                    formik.setFieldValue('subReasonCodeText', value?.label)
                    formik.setFieldTouched('subReasonCode', true, false)
                  }}
                  value={formik.values.subReasonCode}
                  disabled={state.showDocuments}
                  required={formik.values.reasonCode === VI19_REASON_CODE}
                  error={formik.errors.subReasonCode !== undefined}
                  errorText={formik.errors.subReasonCode}
                />
              </Grid.Item>
            </Grid.Container>
          )}

          {/* Sub Reason Code Text 2 - Dynamic Input */}
          {getSubReasonCodeText2Component()}

          <Grid.Container className="hc-pt-normal hc-ml-expanded">
            <Grid.Item xs={10} md={10} className="hc-pb-none hc-pt-none">
              <Form.Field
                id="caseDescription"
                label="Brief explanation for raising the case:"
                type="textarea"
                onChange={formik.handleChange}
                maxLength="500"
                value={formik.values.caseDescription}
                error={formik.errors.caseDescription !== undefined}
                errorText={formik.errors.caseDescription}
                disabled={state.showDocuments}
                size="dense"
              />
            </Grid.Item>
          </Grid.Container>
          {formData?.caseType === CaseType.DISPUTE && (
            <Grid.Container className="hc-pt-none hc-ml-expanded">
              <Grid.Item xs={10} md={10} className="hc-pb-none hc-pt-none">
                <Input.Info
                  error={formik.errors.originalDocumentCreateDate !== undefined}
                >
                  {formik.errors.originalDocumentCreateDate}
                </Input.Info>
              </Grid.Item>
            </Grid.Container>
          )}
        </ExpandableSection.Content>
      </ExpandableSection>
    </div>
  )
}

export default DisputeDetails
